import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Plus, Edit2, Trash2, CheckCircle, XCircle, Star } from 'lucide-react';
import './AdminTestimonials.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminTestimonials = () => {
  const { token } = useAuth();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [filter, setFilter] = useState('all');
  const [formData, setFormData] = useState({
    name: '',
    event: '',
    rating: 5,
    comment: '',
    date: new Date().toISOString().split('T')[0],
    is_approved: true,
    is_active: true
  });

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/admin/testimonials`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTestimonials(response.data.testimonials);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingTestimonial) {
        await axios.put(
          `${API}/admin/testimonials/${editingTestimonial.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${API}/admin/testimonials`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      fetchTestimonials();
      closeModal();
      alert(editingTestimonial ? 'Testimonial updated!' : 'Testimonial added!');
    } catch (error) {
      console.error('Error saving testimonial:', error);
      alert('Failed to save testimonial');
    }
  };

  const deleteTestimonial = async (id) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;

    try {
      await axios.delete(`${API}/admin/testimonials/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchTestimonials();
      alert('Testimonial deleted!');
    } catch (error) {
      console.error('Error deleting testimonial:', error);
      alert('Failed to delete testimonial');
    }
  };

  const toggleApproved = async (testimonial) => {
    try {
      await axios.put(
        `${API}/admin/testimonials/${testimonial.id}`,
        { is_approved: !testimonial.is_approved },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchTestimonials();
    } catch (error) {
      console.error('Error toggling approved:', error);
    }
  };

  const openModal = (testimonial = null) => {
    if (testimonial) {
      setEditingTestimonial(testimonial);
      setFormData({
        name: testimonial.name,
        event: testimonial.event,
        rating: testimonial.rating,
        comment: testimonial.comment,
        date: testimonial.date,
        is_approved: testimonial.is_approved,
        is_active: testimonial.is_active
      });
    } else {
      setEditingTestimonial(null);
      setFormData({
        name: '',
        event: '',
        rating: 5,
        comment: '',
        date: new Date().toISOString().split('T')[0],
        is_approved: true,
        is_active: true
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingTestimonial(null);
  };

  const filteredTestimonials = testimonials.filter(t => {
    if (filter === 'approved') return t.is_approved && t.is_active;
    if (filter === 'pending') return !t.is_approved;
    if (filter === 'inactive') return !t.is_active;
    return true;
  });

  if (loading) {
    return <div className="loading">Loading testimonials...</div>;
  }

  return (
    <div className="admin-testimonials">
      <div className="testimonials-header">
        <div>
          <h2>Testimonials Management</h2>
          <p>Manage customer testimonials and reviews</p>
        </div>
        <button onClick={() => openModal()} className="btn-primary">
          <Plus size={18} /> Add Testimonial
        </button>
      </div>

      <div className="testimonials-filters">
        <button
          onClick={() => setFilter('all')}
          className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
        >
          All ({testimonials.length})
        </button>
        <button
          onClick={() => setFilter('approved')}
          className={`filter-btn ${filter === 'approved' ? 'active' : ''}`}
        >
          Approved ({testimonials.filter(t => t.is_approved && t.is_active).length})
        </button>
        <button
          onClick={() => setFilter('pending')}
          className={`filter-btn ${filter === 'pending' ? 'active' : ''}`}
        >
          Pending ({testimonials.filter(t => !t.is_approved).length})
        </button>
      </div>

      <div className="testimonials-list">
        {filteredTestimonials.map((testimonial) => (
          <div key={testimonial.id} className="testimonial-card">
            <div className="testimonial-header">
              <div className="testimonial-author">
                <div className="author-avatar">
                  {testimonial.name.charAt(0)}
                </div>
                <div>
                  <h3>{testimonial.name}</h3>
                  <p className="testimonial-event">{testimonial.event}</p>
                </div>
              </div>
              <div className="testimonial-meta">
                <div className="testimonial-rating">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      size={16}
                      fill={i < testimonial.rating ? '#fbbf24' : 'none'}
                      color={i < testimonial.rating ? '#fbbf24' : '#d1d5db'}
                    />
                  ))}
                </div>
                <span className="testimonial-date">{testimonial.date}</span>
              </div>
            </div>

            <div className="testimonial-content">
              <p>"{testimonial.comment}"</p>
            </div>

            <div className="testimonial-footer">
              <div className="testimonial-status">
                {testimonial.is_approved ? (
                  <span className="status-approved">
                    <CheckCircle size={14} /> Approved
                  </span>
                ) : (
                  <span className="status-pending">
                    Pending Approval
                  </span>
                )}
                {!testimonial.is_active && (
                  <span className="status-inactive">Inactive</span>
                )}
              </div>

              <div className="testimonial-actions">
                {!testimonial.is_approved && (
                  <button
                    onClick={() => toggleApproved(testimonial)}
                    className="btn-approve"
                  >
                    <CheckCircle size={16} /> Approve
                  </button>
                )}
                {testimonial.is_approved && (
                  <button
                    onClick={() => toggleApproved(testimonial)}
                    className="btn-icon"
                  >
                    <XCircle size={16} /> Unapprove
                  </button>
                )}
                <button
                  onClick={() => openModal(testimonial)}
                  className="btn-icon"
                >
                  <Edit2 size={16} />
                </button>
                <button
                  onClick={() => deleteTestimonial(testimonial.id)}
                  className="btn-icon btn-danger"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredTestimonials.length === 0 && (
        <div className="empty-state">
          <p>No testimonials in this category yet.</p>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingTestimonial ? 'Edit Testimonial' : 'Add Testimonial'}</h3>
              <button onClick={closeModal} className="modal-close">&times;</button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Customer Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Event Type *</label>
                  <input
                    type="text"
                    value={formData.event}
                    onChange={(e) => setFormData({ ...formData, event: e.target.value })}
                    placeholder="e.g., Anniversary, Birthday"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Rating *</label>
                  <select
                    value={formData.rating}
                    onChange={(e) => setFormData({ ...formData, rating: parseInt(e.target.value) })}
                    required
                  >
                    <option value={5}>5 Stars - Excellent</option>
                    <option value={4}>4 Stars - Very Good</option>
                    <option value={3}>3 Stars - Good</option>
                    <option value={2}>2 Stars - Fair</option>
                    <option value={1}>1 Star - Poor</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Date *</label>
                  <input
                    type="text"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    placeholder="March 2024"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Testimonial *</label>
                <textarea
                  value={formData.comment}
                  onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                  rows="4"
                  placeholder="Customer's review..."
                  required
                />
              </div>

              <div className="form-checkboxes">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_approved}
                    onChange={(e) => setFormData({ ...formData, is_approved: e.target.checked })}
                  />
                  <span>Approved (show on website)</span>
                </label>
                
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  <span>Active</span>
                </label>
              </div>

              <div className="modal-actions">
                <button type="button" onClick={closeModal} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingTestimonial ? 'Update' : 'Add'} Testimonial
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTestimonials;
